import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye, Star } from 'lucide-react';
import { getProductImageSrc, handleImageError } from '../../utils/product';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getDiscountedPrice, getDiscountPercent, formatPrice } from '../../utils/priceUtils';

const ProductCard = ({ product }) => {
    const { t } = useTranslation();
    const imageSrc = getProductImageSrc(product);

    // Calculate Pricing
    const originalPrice = Number(product.price);
    const hasDiscount = product.discount && product.discount.isActive;
    const finalPrice = getDiscountedPrice(product);
    const discountPercent = getDiscountPercent(product);

    return (
        <div className="group relative bg-fecos-surface rounded-2xl border border-fecos-border shadow-sm hover:shadow-xl hover:border-fecos-accent/20 transition-all duration-300 flex flex-col h-full overflow-hidden">

            {/* NEW Image Container */}
            <div className="relative w-full rounded-2xl bg-fecos-soft/60 border border-fecos-border overflow-hidden group-hover:border-fecos-accent/30 transition-colors">

                {/* Badge */}
                {hasDiscount && discountPercent > 0 && (
                    <div className="absolute top-3 left-3 bg-red-600 text-white text-[10px] uppercase font-black px-2 py-1 rounded-md z-10 shadow-sm animate-pulse">
                        -{discountPercent}%
                    </div>
                )}

                {/* Aspect Ratio Box */}
                <div className="aspect-[4/5] sm:aspect-[1/1] w-full flex items-center justify-center p-4">
                    <Link to={`/product/${product.id}`} className="w-full h-full flex items-center justify-center">
                        <img
                            src={imageSrc}
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            className="max-h-full max-w-full object-contain drop-shadow-sm group-hover:scale-110 transition-transform duration-500 ease-in-out"
                            onError={handleImageError}
                        />
                    </Link>
                </div>

                {/* Hover Actions */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 z-20">
                    <Link
                        to={`/product/${product.id}`}
                        className="p-3 bg-white text-gray-800 rounded-full shadow-lg hover:bg-fecos-accent hover:text-white transition-colors border border-gray-100"
                        title={t('common.details', 'Details')}
                    >
                        <Eye className="w-5 h-5" />
                    </Link>
                    <button
                        className="p-3 bg-white text-gray-800 rounded-full shadow-lg hover:bg-fecos-accent hover:text-white transition-colors border border-gray-100"
                        title={t('common.addToCart', 'Add to Cart')}
                    >
                        <ShoppingCart className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 flex flex-col flex-1">
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-extrabold text-fecos-accent/80 uppercase tracking-widest bg-fecos-accent/5 px-2 py-1 rounded">
                        {product.category || t('common.supplement', 'Supplement')}
                    </span>
                </div>

                <Link to={`/product/${product.id}`} className="block mb-2">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-fecos-accent transition-colors line-clamp-2 leading-tight h-[2.5em] sm:h-[3em] flex items-start">
                        {product.name}
                    </h3>
                </Link>

                <div className="mt-auto flex items-end justify-between border-t border-gray-50 pt-3 sm:pt-4">
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-400 font-medium">{t('common.price', 'Price')}</span>
                        <div className="flex items-baseline gap-2">
                            {hasDiscount ? (
                                <>
                                    <span className="text-xs sm:text-sm text-gray-400 line-through decoration-red-500/50 decoration-2">{formatPrice(product.price)}</span>
                                    <span className="text-lg sm:text-xl font-black text-red-600">{formatPrice(finalPrice)}</span>
                                </>
                            ) : (
                                <span className="text-lg sm:text-xl font-black text-fecos-dark">{formatPrice(product.price)}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
